import { suggestChanges } from "@handlewithcare/prosemirror-suggest-changes";
import { reactKeys } from "@handlewithcare/react-prosemirror";
import { collab } from "@stepwisehq/prosemirror-collab-commit/collab-commit";
import { history } from "prosemirror-history";
import { keymap } from "prosemirror-keymap";
import { type Schema } from "prosemirror-model";
import { type Plugin } from "prosemirror-state";

import { generateShortUuid } from "@moment/api-collab";

import { baseKeymap } from "../commands/baseKeymap";
import { keyMap } from "../commands/keymap";
import { buildInputRules } from "../rules";
import { appendixPlugin } from "./appendix-plugin/appendixPlugin";
import { dropCursorPlugin } from "./dropCursorPlugin";
import { highlightPlugin } from "./highlight-plugin/highlightPlugin";
import { imagePlaceholderPlugin } from "./image-placeholder-plugin/imagePlaceholderPlugin";
import { imageDropPlugin } from "./imageDropPlugin";
import { pageLinkAutocompletePlugin } from "./page-link-autocomplete";
import { preserveReadonlySectionPlugin } from "./preserve-readonly-section-plugin/preserveReadonlySectionPlugin";
import { textPlaceholderPlugin } from "./text-placeholder-plugin/textPlaceholderPlugin";

export const prosemirrorClientId = generateShortUuid();

export type Options = {
	documentId: string;
	pageId: string;
	schema: Schema;
	readonly: boolean;
	version?: number;
};

// !! This module exports helper functions for deriving a set of basic
// menu items, input rules, or key bindings from a schema. These
// values need to know about the schema for two reasons—they need
// access to specific instances of node and mark types, and they need
// to know which of the node and mark types that they know about are
// actually present in the schema.
//
// The `exampleSetup` plugin ties these together into a plugin that
// will automatically enable this basic functionality in an editor.

// A convenience plugin that bundles together a simple menu with basic
// key bindings, input rules, and styling for the example schema.
// Probably only useful for quickly setting up a passable
// editor—you'll need more control over your settings in most
// real-world situations.
//
//   options::- The following options are recognized:
//
//     schema:: Schema
//     The schema to generate key bindings and menu items for.
//
//     history:: ?bool
//     Set to false to disable the history plugin.
//
//     menuBar:: ?bool
//     Set to false to disable the menu bar.

export function createPlugins({
	documentId,
	pageId,
	schema,
	readonly,
	version,
}: Options): Plugin[] {
	const plugins: Plugin[] = [];

	plugins.push(imageDropPlugin(documentId, pageId, typeof version === "number"));

	plugins.push(buildInputRules(schema));

	//
	// IMPORTANT: Base keymap needs to come before anything. Add-ons can interfere, and
	// generally plugins assume the base keymap has already terminated before executing.
	//

	plugins.push(keymap(baseKeymap(schema)));

	//
	// Custom keymap.
	//

	plugins.push(keymap(keyMap()));

	//
	// Everything else.
	//

	// Prevents user-initiated transactions from deleting the moment-managed
	// `readonly_section` (the template at the top of AGENTS.md). Installed as a
	// `filterTransaction` plugin so it catches every entry point — Backspace,
	// Delete, Cmd-A + typing, paste-over, drag-and-drop, etc. — not just the
	// specific key bindings that used to be intercepted.
	plugins.push(preserveReadonlySectionPlugin());

	plugins.push(appendixPlugin(schema));

	plugins.push(highlightPlugin());

	plugins.push(dropCursorPlugin());

	plugins.push(imagePlaceholderPlugin());

	if (!readonly) {
		plugins.push(textPlaceholderPlugin());
		plugins.push(pageLinkAutocompletePlugin());
	}

	// This use to be wrapped around a a feature flag,
	// but now we cannot call feature flags unless we are in are in a React Component.
	// If we want feature flags outside of reqct, we need to initialize the state machine in a different way.
	// plugins.push(visualDebugPlugin());

	plugins.push(history({ newGroupDelay: 250 }));

	plugins.push(suggestChanges());

	plugins.push(reactKeys());

	if (typeof version === "number") {
		plugins.push(collab({ version, clientID: prosemirrorClientId }));
	}

	return plugins;
}
